/**
 * Company Switcher Component
 *
 * Composant pour permettre aux utilisateurs de switcher
 * entre leurs differents etablissements.
 *
 * Les donnees (companies, activeCompany) sont lues depuis le session store
 * (source de verite unique). Aucun appel SWR dedie : le SessionProvider
 * garantit que les donnees sont pretes avant le mount des enfants.
 *
 * @module CompanySwitcher
 * @author Equipe Brydge
 * @since 3.0.0
 */

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  SwapHoriz as SwitchIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {useCompanySwitcher} from 'packages/common/hooks/auth';
import {useAdminAccess} from 'packages/common/hooks/useAdminAccess';

// ============================================
// Composant principal CompanySwitcher
// ============================================
export const CompanySwitcher = ({
  variant = 'button',
  showLabel = true,
  showChip = true,
  size = 'medium',
  ...other
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingTo, setSwitchingTo] = useState(null);

  const {
    companies,
    activeCompany,
    totalCompanies,
    switchCompany,
    hasMultipleCompanies,
    isCompanyActive,
  } = useCompanySwitcher();

  const {isAdminAccessMode} = useAdminAccess();

  const handleClick = (event) => {
    // Bloquer en mode admin access
    if (isAdminAccessMode) return;
    if (!hasMultipleCompanies) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitch = async (company) => {
    if (isCompanyActive(company._id) || switchingTo) return;

    setSwitchingTo(company._id);
    try {
      await switchCompany(company._id, company.name, company.slug);
      handleClose();
    } catch (error) {
      console.error('Erreur switch:', error);
    } finally {
      setSwitchingTo(null);
    }
  };

  // Ne pas afficher si pas de multiple companies ou pas d'active company
  if (!hasMultipleCompanies || !activeCompany) {
    return null;
  }

  const isOpen = Boolean(anchorEl);

  // Helper pour obtenir les styles d'avatar selon si logo existe ou non
  const getAvatarStyles = (company, size) => {
    const hasLogo = company.logoPath && company.logoPath.length > 0;

    return {
      width: size,
      height: size,
      backgroundColor: hasLogo ? '#FFFFFF' : company.primaryColor || '#3B82F6',
      color: hasLogo ? 'transparent' : 'white',
      fontSize: size === 24 ? '0.75rem' : '0.875rem',
      fontWeight: 600,
    };
  };

  // Variant mini : bouton compact avec nom pour header mobile
  if (variant === 'mini') {
    return (
      <Box {...other}>
        <Tooltip
          title={isAdminAccessMode ? 'Changement d\'établissement bloqué en mode admin access' : ''}
        >
          <Button
            onClick={handleClick}
            disabled={isAdminAccessMode}
            sx={{
              'minWidth': 'auto',
              'maxWidth': 140,
              'px': 1,
              'py': 0.5,
              'borderRadius': 2,
              'backgroundColor': 'rgba(255, 255, 255, 0.15)',
              'border': '1px solid rgba(255, 255, 255, 0.25)',
              'textTransform': 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
            }}
          >
            <Avatar
              variant="rounded"
              src={activeCompany.logoPath}
              sx={{
                ...getAvatarStyles(activeCompany, 24),
                borderRadius: 1,
              }}
            >
              {activeCompany.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              noWrap
              sx={{
                ml: 0.75,
                color: 'white',
                fontWeight: 500,
                fontSize: '0.8rem',
                maxWidth: 80,
              }}
            >
              {activeCompany.name}
            </Typography>
            {hasMultipleCompanies && !isAdminAccessMode && (
              <ArrowDownIcon sx={{ml: 0.25, fontSize: 16, color: 'white', opacity: 0.8}} />
            )}
          </Button>
        </Tooltip>
        {!isAdminAccessMode && renderMenu()}
      </Box>
    );
  }

  // Variants de rendu
  if (variant === 'icon') {
    return (
      <Box {...other}>
        <Tooltip
          title={
            isAdminAccessMode ?
              'Changement d\'établissement bloqué en mode admin access' :
              `Établissement actif: ${activeCompany.name}`
          }
        >
          <IconButton
            onClick={handleClick}
            size={size}
            disabled={isAdminAccessMode}
            sx={{
              'color': 'primary.main',
              '&:hover': {
                backgroundColor: alpha('#000', 0.08),
              },
            }}
          >
            <BusinessIcon />
            {hasMultipleCompanies && !isAdminAccessMode && (
              <ArrowDownIcon sx={{ml: 0.5, fontSize: '0.8em'}} />
            )}
          </IconButton>
        </Tooltip>
        {!isAdminAccessMode && renderMenu()}
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Box {...other}>
        <Tooltip
          title={isAdminAccessMode ? 'Changement d\'établissement bloqué en mode admin access' : ''}
        >
          <span>
            <Button
              onClick={handleClick}
              variant="outlined"
              size={size}
              fullWidth={true}
              disabled={isAdminAccessMode}
              endIcon={hasMultipleCompanies && !isAdminAccessMode ? <ArrowDownIcon /> : null}
              sx={{
                'textTransform': 'none',
                'borderRadius': 0.8,
                'borderColor': 'rgba(255, 255, 255, 0.2)',
                'color': '#E2E8F0',
                'maxWidth': '100%',
                'overflow': 'hidden',
                'px': 1.5,
                'py': 0.75,
                'justifyContent': 'flex-start',
                'textAlign': 'left',
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                },
                '&:hover': {
                  borderColor: (theme) => theme.palette.primary.light,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              <Box sx={{display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 0.75}}>
                <Typography variant="body2" noWrap sx={{minWidth: 0, color: '#E2E8F0'}}>
                  {activeCompany.name}
                </Typography>
                {showChip && totalCompanies > 1 && (
                  <Chip
                    label={totalCompanies}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                      backgroundColor: 'slate.main',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </Button>
          </span>
        </Tooltip>
        {!isAdminAccessMode && renderMenu()}
      </Box>
    );
  }

  // Variant par défaut: 'button'
  return (
    <Box {...other}>
      <Button
        onClick={handleClick}
        variant="text"
        size={size}
        startIcon={
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: activeCompany.primaryColor || 'primary.main',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {activeCompany.name?.charAt(0)?.toUpperCase()}
          </Avatar>
        }
        endIcon={hasMultipleCompanies ? <ArrowDownIcon /> : null}
        sx={{
          'textTransform': 'none',
          'color': 'text.primary',
          'px': 2,
          'py': 1,
          'borderRadius': 2,
          '&:hover': {
            backgroundColor: alpha('#000', 0.04),
          },
        }}
      >
        <Box sx={{textAlign: 'left', ml: 1, minWidth: 0, flex: 1}}>
          {showLabel && (
            <Typography variant="caption" color="text.secondary" display="block">
              Établissement
            </Typography>
          )}
          <Typography variant="body2" fontWeight={500} noWrap>
            {activeCompany.name}
          </Typography>
        </Box>
        {showChip && totalCompanies > 1 && (
          <Chip
            label={`${totalCompanies} établissements`}
            size="small"
            variant="outlined"
            sx={{
              ml: 2,
              height: 24,
              fontSize: '0.75rem',
              borderColor: alpha('#000', 0.12),
            }}
          />
        )}
      </Button>
      {renderMenu()}
    </Box>
  );

  // ============================================
  // Rendu du menu déroulant
  // ============================================
  function renderMenu() {
    return (
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(15, 28, 46, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            bgcolor: (theme) => theme.palette.navy[900],
            backgroundImage: 'none',
          },
        }}
      >
        {/* En-tête du menu */}
        <Box
          sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'rgba(255, 255, 255, 0.12)'}}
        >
          <Typography variant="subtitle2" sx={{color: '#FFF'}}>
            Changer d&apos;établissement
          </Typography>
          <Typography variant="caption" sx={{color: '#f1f1f1'}}>
            {totalCompanies} établissements disponibles
          </Typography>
        </Box>

        {/* Liste des companies */}
        {companies.map((company) => {
          const isActive = isCompanyActive(company._id);
          const isSwitching = switchingTo === company._id;

          return (
            <MenuItem
              key={company._id}
              onClick={() => handleSwitch(company)}
              disabled={isActive || isSwitching}
              sx={{
                'px': 2,
                'py': 1.5,
                'minHeight': 56,
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
                '&:hover:not(.Mui-disabled)': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{minWidth: 44}}>
                <Avatar src={company.logoPath} sx={getAvatarStyles(company, 36)}>
                  {company.name?.charAt(0)?.toUpperCase()}
                </Avatar>
              </ListItemIcon>

              <ListItemText
                sx={{minWidth: 0, flex: 1}}
                primary={
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 900 : 400}
                      noWrap
                      sx={{flex: 1, minWidth: 0, color: '#FFFFFF'}}
                    >
                      {company.name}
                    </Typography>
                    {isActive && (
                      <Chip
                        label="Actuel"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          flexShrink: 0,
                          bgcolor: (theme) => theme.palette.primary.main,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" noWrap sx={{color: 'rgba(226, 232, 240, 0.7)'}}>
                    {company.activitySectors?.join(', ') || 'Restaurant'}
                  </Typography>
                }
              />

              {/* Icône d'état */}
              <Box sx={{ml: 1, minWidth: 24, display: 'flex', justifyContent: 'center'}}>
                {isSwitching ? (
                  <CircularProgress size={16} sx={{color: '#FFFFFF'}} />
                ) : isActive ? (
                  <CheckIcon sx={{fontSize: 20, color: '#FFFFFF'}} />
                ) : (
                  <SwitchIcon sx={{fontSize: 20, color: 'rgba(255, 255, 255, 0.5)'}} />
                )}
              </Box>
            </MenuItem>
          );
        })}

        {/* Footer optionnel */}
        <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />
        <Box sx={{px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5}}>
          <HistoryIcon sx={{fontSize: 16, color: 'rgba(255, 255, 255, 0.6)'}} />
          <Typography variant="caption" sx={{color: '#FFFFFF'}}>
            Le changement prend effet immédiatement
          </Typography>
        </Box>
      </Menu>
    );
  }
};

CompanySwitcher.propTypes = {
  variant: PropTypes.oneOf(['button', 'compact', 'icon', 'mini']),
  showLabel: PropTypes.bool,
  showChip: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default CompanySwitcher;
